import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import {
  PIPELINE_STAGES, currency, fmtDate, fmtDateTime, countryFlag, STAGE_BY_ID,
} from "@/lib/format";
import {
  ArrowLeft, Upload, FileText, Trash2, Download, Plus, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

const DOC_CATEGORIES = [
  { id: "passport", label: "Passport" },
  { id: "visa", label: "Visa" },
  { id: "medical_report", label: "Medical Report" },
  { id: "insurance", label: "Insurance" },
  { id: "prescription", label: "Prescription" },
  { id: "invoice", label: "Invoice" },
  { id: "general", label: "General" },
];

export default function PatientDetail({ role }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [patient, setPatient] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [tab, setTab] = useState("overview");
  const [docCategory, setDocCategory] = useState("medical_report");
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const [p, t, d, h, doc] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get(`/patients/${id}/timeline`),
        api.get(`/documents?patient_id=${id}`),
        api.get(`/hospitals`),
        api.get(`/doctors`),
      ]);
      setPatient(p.data); setTimeline(t.data); setDocuments(d.data);
      setHospitals(h.data); setDoctors(doc.data);
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const updateStage = async (stage) => {
    try {
      const { data } = await api.patch(`/patients/${id}`, { stage });
      setPatient(data);
      const t = await api.get(`/patients/${id}/timeline`); setTimeline(t.data);
      toast.success(`Stage updated`);
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("File must be under 4MB"); return; }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    try {
      await api.post("/documents", {
        patient_id: id, name: file.name, category: docCategory,
        content_base64: b64, mime_type: file.type || "application/octet-stream",
      });
      e.target.value = "";
      load(); toast.success("Document uploaded");
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Delete document?")) return;
    try { await api.delete(`/documents/${docId}`); load(); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const downloadDoc = (docId) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}`, "_blank");
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/patients/${id}/timeline`, {
        title: "Note added", description: note, event_type: "note",
      });
      setNote("");
      const t = await api.get(`/patients/${id}/timeline`); setTimeline(t.data);
      toast.success("Note added");
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  if (!patient) return <div className="text-sm text-[#5C6660]">Loading…</div>;

  const hospital = hospitals.find((h) => h.id === patient.hospital_id);
  const doctor = doctors.find((d) => d.id === patient.doctor_id);
  const stageIdx = PIPELINE_STAGES.findIndex((s) => s.id === patient.stage);

  return (
    <div data-testid="patient-detail">
      <Link to={`/${role}/patients`} className="inline-flex items-center gap-1.5 text-xs text-[#5C6660] hover:text-[#2A4D3E] mb-4">
        <ArrowLeft size={14} /> Back to patients
      </Link>
      <PageHeader
        eyebrow={`${countryFlag(patient.country)} ${patient.country || "Unknown origin"}`}
        title={patient.name}
        subtitle={`${patient.treatment} · ${currency(patient.treatment_value)}`}
        actions={
          role !== "patient" && (
            <Link to={`/${role}/quotations/new?patient_id=${patient.id}`}
              className="bg-[#D97757] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#C26243] flex items-center gap-2"
              data-testid="patient-create-quote">
              <FileText size={15} /> Create quotation
            </Link>
          )
        }
      />

      {/* Stage bar */}
      {role !== "patient" && (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-wider text-[#8E9992] font-semibold">Stage</span>
              <StatusBadge kind="stage" value={patient.stage} />
            </div>
            <div className="text-xs text-[#8E9992]">{stageIdx + 1} of {PIPELINE_STAGES.length}</div>
          </div>
          <div className="h-1.5 bg-[#F2F4F3] rounded-full overflow-hidden mb-3">
            <div className="h-full bg-[#2A4D3E] transition-all" style={{ width: `${((stageIdx + 1) / PIPELINE_STAGES.length) * 100}%` }} />
          </div>
          <div className="flex flex-wrap gap-2">
            {PIPELINE_STAGES.filter((s) => ["Treatment", "Closure"].includes(s.phase)).map((s) => (
              <button key={s.id} onClick={() => updateStage(s.id)} data-testid={`patient-stage-${s.id}`}
                style={{ borderColor: s.id === patient.stage ? s.color : "#E8E6E1", color: s.id === patient.stage ? s.color : "#5C6660", background: s.id === patient.stage ? s.bg : "#fff" }}
                className="text-[11px] px-3 py-1.5 rounded-full border font-medium hover:border-[#2A4D3E] hover:text-[#2A4D3E] transition">
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 border-b border-[#E8E6E1] mb-6">
        {["overview", "documents", "timeline"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`px-4 py-2.5 text-sm font-medium capitalize border-b-2 transition ${tab === t ? "border-[#2A4D3E] text-[#2A4D3E]" : "border-transparent text-[#5C6660] hover:text-[#2A332C]"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-[#E8E6E1] rounded-2xl p-6">
            <div className="font-heading font-semibold text-lg mb-4 text-[#2A332C]">Profile</div>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <Info label="Email" value={patient.email || "—"} />
              <Info label="Phone" value={patient.phone || "—"} />
              <Info label="Age / Gender" value={`${patient.age || "—"} ${patient.gender ? "· " + patient.gender : ""}`} />
              <Info label="Country of origin" value={`${countryFlag(patient.country)} ${patient.country || "—"}`} />
              <Info label="Treatment" value={patient.treatment} />
              <Info label="Hospital" value={hospital?.name || "—"} />
              <Info label="Doctor" value={doctor?.name || "—"} />
              <Info label="Treatment value" value={currency(patient.treatment_value)} />
              <Info label="Arrival date" value={fmtDate(patient.arrival_date)} />
              <Info label="Discharge date" value={fmtDate(patient.discharge_date)} />
            </dl>
            {patient.notes && (
              <div className="mt-6 pt-6 border-t border-[#E8E6E1]">
                <div className="text-xs uppercase tracking-wider text-[#8E9992] font-semibold mb-2">Notes</div>
                <div className="text-sm text-[#2A332C] whitespace-pre-wrap">{patient.notes}</div>
              </div>
            )}
          </div>
          <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6 h-fit">
            <div className="font-heading font-semibold text-lg mb-3 text-[#2A332C]">Quick add note</div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows="3" placeholder="Add a note for the team…"
              data-testid="patient-note-input"
              className="w-full border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] text-sm focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]" />
            <button onClick={addNote} data-testid="patient-add-note"
              className="mt-3 w-full bg-[#2A4D3E] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#1E3B2F] flex items-center justify-center gap-1.5">
              <MessageSquare size={14} /> Add note
            </button>
          </div>
        </div>
      )}

      {tab === "documents" && (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6" data-testid="patient-documents">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="font-heading font-semibold text-lg text-[#2A332C]">Travel & medical documents</div>
            <div className="flex items-center gap-2">
              <select value={docCategory} onChange={(e) => setDocCategory(e.target.value)} data-testid="doc-category-select"
                className="border border-[#E8E6E1] rounded-full px-4 py-2 text-sm bg-white focus:outline-none focus:border-[#2A4D3E]">
                {DOC_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <input ref={fileRef} type="file" hidden onChange={uploadFile} data-testid="upload-file-input" />
              <button onClick={() => fileRef.current?.click()} data-testid="upload-document-button"
                className="bg-[#2A4D3E] text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-[#1E3B2F] flex items-center gap-1.5">
                <Upload size={14} /> Upload
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {documents.map((d) => (
              <div key={d.id} className="border border-[#E8E6E1] rounded-xl p-4 hover:shadow-sm transition" data-testid={`doc-${d.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#E6F0EB] flex items-center justify-center flex-shrink-0">
                    <FileText size={16} className="text-[#2A4D3E]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#2A332C] truncate">{d.name}</div>
                    <div className="text-[11px] text-[#8E9992] mt-0.5 capitalize">{d.category.replace("_", " ")} · {fmtDate(d.uploaded_at)}</div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => downloadDoc(d.id)} className="flex-1 border border-[#E8E6E1] rounded-full text-xs py-1.5 hover:border-[#2A4D3E] hover:text-[#2A4D3E] flex items-center justify-center gap-1">
                    <Download size={12} /> Download
                  </button>
                  {role !== "patient" && role !== "partner" && (
                    <button onClick={() => deleteDoc(d.id)} className="border border-[#E8E6E1] rounded-full px-2.5 py-1.5 hover:border-[#C0392B] hover:text-[#C0392B]">
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {documents.length === 0 && (
              <div className="col-span-full text-center text-sm text-[#8E9992] py-12">No documents uploaded yet.</div>
            )}
          </div>
        </div>
      )}

      {tab === "timeline" && (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
          <div className="font-heading font-semibold text-lg mb-5 text-[#2A332C]">Journey timeline</div>
          <div className="relative border-l-2 border-[#E8E6E1] ml-2 space-y-6">
            {timeline.map((t) => (
              <div key={t.id} className="relative pl-6">
                <div className="absolute w-3 h-3 rounded-full bg-[#2A4D3E] -left-[7px] top-1 border-2 border-white" />
                <div className="text-sm font-medium text-[#2A332C]">{t.title}</div>
                {t.description && <div className="text-xs text-[#5C6660] mt-0.5">{t.description}</div>}
                <div className="text-[10px] text-[#8E9992] mt-1.5">{fmtDateTime(t.created_at)} · {t.actor_name || "System"}</div>
              </div>
            ))}
            {timeline.length === 0 && <div className="text-sm text-[#8E9992] pl-4">No timeline events yet.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#8E9992] font-semibold mb-0.5">{label}</div>
      <div className="text-sm text-[#2A332C]">{value}</div>
    </div>
  );
}
